/**
 * BLU-1400: a client's copies of its 3PL's garments (supply aliases) grouped
 * for the read-only section on Products: by 3PL, then by product code, then
 * the sizes and colours in the order the server sent them (by SKU).
 *
 * Pure: the rows come from `inventory.listProductsGrouped` (`supplyAliasSkus`),
 * which already carries each row's 3PL and prices. Nothing is re-derived here
 * except the code and a display name, and the name is a text rule over the
 * rows' own titles, never decoded from the SKU (garment identity is the title).
 */
import { baseGarmentCodeOfSku } from '@/features/base-garments/lib/base-garments-url';
import type { RouterOutputs } from '@/lib/trpc';

/** One supply alias row of the Products read, as the server sends it. */
export type SupplyAliasRow = RouterOutputs['inventory']['listProductsGrouped']['supplyAliasSkus'][number];

/** The 3PL behind a supply alias, as the server sends it. */
export type SupplyOwnerInfo = SupplyAliasRow['supplyOwner'];

/**
 * A 3PL price as text: the amount with the 3PL's own currency code
 * ("4.00 USD", the code beside the amount as on the 3PL bill), so a USD price
 * never reads as the client's dollars. With no code known, the bare amount:
 * never the viewer's currency.
 */
export const formatSupplyPrice = (value: number, currency: string | null): string =>
  currency ? `${value.toFixed(2)} ${currency}` : value.toFixed(2);

export type SupplyAliasCodeGroup = {
  code: string;
  /** The shared title prefix of every variant, else null (the row shows the code alone). */
  name: string | null;
  /** The first variant's image. */
  imageUrl: string | null;
  variants: SupplyAliasRow[];
};

export type SupplyAliasOwnerGroup = SupplyOwnerInfo & {
  /** Stable key for React and for the section's expand state. */
  key: string;
  count: number;
  codes: SupplyAliasCodeGroup[];
};

const NAME_SEPARATOR = ' - ';
const UNKNOWN_OWNER_KEY = 'unknown';

// Push rules use the stricter `garmentCodeOf` (case-sensitive, 2-3 segments): a lowercase or 4-segment alias groups here but stays loose on Skip or Always 0.
const codeOf = (sku: string): string => baseGarmentCodeOfSku(sku) ?? sku.split('-')[0] ?? sku;

/**
 * "Box tee" when every title reads "Box tee - ...", else null. Taken from the
 * first variant's title up to its first " - ".
 */
export function sharedName(variants: ReadonlyArray<{ title: string | null }>): string | null {
  const first = variants[0]?.title ?? '';
  const cut = first.indexOf(NAME_SEPARATOR);
  if (cut <= 0) return null;
  const name = first.slice(0, cut);
  const prefix = `${name}${NAME_SEPARATOR}`;
  return variants.every((v) => (v.title ?? '').startsWith(prefix)) ? name : null;
}

export function groupSupplyAliases(rows: SupplyAliasRow[]): SupplyAliasOwnerGroup[] {
  const owners = new Map<string, { owner: SupplyOwnerInfo; codes: Map<string, SupplyAliasRow[]>; count: number }>();
  for (const row of rows) {
    const key = row.supplyOwner.organizationId ?? UNKNOWN_OWNER_KEY;
    let owner = owners.get(key);
    if (!owner) {
      owner = { owner: row.supplyOwner, codes: new Map(), count: 0 };
      owners.set(key, owner);
    }
    const code = codeOf(row.sku);
    const variants = owner.codes.get(code) ?? [];
    variants.push(row);
    owner.codes.set(code, variants);
    owner.count += 1;
  }

  return [...owners.entries()]
    .map(([key, { owner, codes, count }]) => ({
      ...owner,
      key,
      count,
      codes: [...codes.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([code, variants]) => ({
          code,
          name: sharedName(variants),
          imageUrl: variants[0]?.imageUrl ?? null,
          variants,
        })),
    }))
    .sort((a, b) => {
      // The unknown 3PL (a released mapping) always comes last.
      if ((a.organizationId === null) !== (b.organizationId === null)) return a.organizationId === null ? 1 : -1;
      return (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });
    });
}
